import clsx from 'clsx'
import React, { useRef, useState, DragEvent, ChangeEvent } from 'react'

import { ReactComponent as DocumentIcon } from 'assets/icons/file.svg'

import styles from './FileUpload.module.scss'

interface FileUploadProps {
  acceptConfig?: Record<string, string[]>
  maxFiles?: number
  maxSizeMB?: number
  className?: string
  'data-testid'?: string
  onChange: (file: File | undefined) => void
}

export function FileUpload({
  acceptConfig,
  maxFiles = 1,
  maxSizeMB = 50,
  className,
  'data-testid': testId,
  onChange,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const acceptString = acceptConfig ? Object.values(acceptConfig).flat().join(',') : undefined
  const acceptedExtensions = acceptConfig
    ? Object.values(acceptConfig)
        .flat()
        .map(ext => ext.replace('.', '').toUpperCase())
        .join(', ')
    : 'any'

  const validateAndEmit = (files: FileList | null) => {
    setError(null)
    if (!files || files.length === 0) return

    const file = files[0]

    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      setError(`File must be ${maxSizeMB} MB or less.`)
      return
    }

    if (acceptConfig) {
      const allowed = Object.values(acceptConfig).flat()
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!allowed.includes(ext)) {
        setError(`Only ${acceptedExtensions} files are allowed.`)
        return
      }
    }

    onChange(file)
  }

  const handleClick = () => inputRef.current?.click()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    validateAndEmit(e.target.files)
    e.target.value = '' // reset so same file can be re-selected
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    validateAndEmit(e.dataTransfer.files)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  return (
    <div className={clsx(styles.wrapper, className)} data-testid={testId}>
      <div
        role="button"
        tabIndex={0}
        aria-label="File upload area. Click or drag a file here."
        className={clsx(styles.dropzone, isDragging && styles.dragging)}
        onClick={handleClick}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleClick() : undefined)}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className={styles.iconWrapper} aria-hidden="true">
          <DocumentIcon className={styles.icon} />
        </div>

        <p className={styles.label}>
          <span className={styles.chooseText}>Choose a file </span>
          <span className={styles.dragText}>to upload or drag it here</span>
        </p>

        <p className={styles.hint}>
          Only .{acceptedExtensions.toLowerCase()} file at {maxSizeMB} MB or less
        </p>

        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          multiple={maxFiles > 1}
          className={styles.hiddenInput}
          onChange={handleChange}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </div>
  )
}
